import React, { useCallback } from "react";
import PT from "prop-types";
import {
  LeftCol,
  RightCol,
  Logo,
  Notifier,
  NotifierCount,
  Wrap,
  NotifierWrap,
  RoleSpecificLink,
  RoleSpecificLinks,
} from "./styled";
import { Link } from "react-router-dom";
import Notifications from "../Notifications/container";
import MessagesPageLoadable from "_p/messages/loadable";
import { Helmet } from "_c/Helmet";
import ThemeToggler from "../ThemeToggler";
import UserProfile from "../UserProfile";
import { preloadModules } from "@/utils/loadable";
import { toggleNotificationsAtom } from "../Notifications/atoms/visibility";
import { useUpdateAtom } from "jotai/utils";
import { analyticsAction } from "@/utils/analytics";

const AuthHeader = ({ t, ...props }) => {
  const toggleNotifications = useUpdateAtom(toggleNotificationsAtom);
  const onMessagesHover = useCallback(() => {
    preloadModules([MessagesPageLoadable]);
  }, []);
  const hasMessagesOrNotifications =
    props.freshMessagesCount + props.freshNotificationsCount > 0;
  return (
    <>
      <Helmet
        link={[
          {
            rel: "shortcut icon",
            href: hasMessagesOrNotifications
              ? "/favicon-new.png"
              : "/favicon.png",
            type: "image/x-icon",
          },
        ]}
      />
      <Wrap>
        <LeftCol>
          <Link style={{ display: "block" }} to="/">
            <Logo />
          </Link>
          <RoleSpecificLinks>
            {props.roleSpecificLinks.map(({ route, Icon, key }) => (
              <li key={key}>
                <RoleSpecificLink
                  to={route}
                  onClick={
                    key === "add_task" ? () => props.openCreateTask() : null
                  }
                  as={key === "add_task" ? "button" : null}
                >
                  <Icon color="currentColor" width="24" />
                  <span>{t(`common:profile_links.${key}`)}</span>
                </RoleSpecificLink>
              </li>
            ))}
          </RoleSpecificLinks>
        </LeftCol>
        <RightCol>
          {/*<RefreshButton />*/}
          <ThemeToggler />
          <NotifierWrap>
            <Notifier
              onMouseEnter={onMessagesHover}
              onTouchStart={onMessagesHover}
              title={t("profile_upper_menu.messages")}
              as={Link}
              to={props.messengerLink.route}
              onClick={() => {
                analyticsAction({
                  category: "header",
                  action: "click_messages",
                });
              }}
            >
              <MessagesIcon color="white" width="17" />
            </Notifier>
            {props.freshMessagesCount > 0 && (
              <NotifierCount>{props.freshMessagesCount}</NotifierCount>
            )}
          </NotifierWrap>
          <NotifierWrap>
            <Notifier
              title={t("profile_upper_menu.notifications")}
              onClick={() => {
                analyticsAction({
                  category: "header",
                  action: "click_notifications",
                });
                toggleNotifications();
              }}
            >
              <NotificationsIcon color="white" width="17" />
            </Notifier>
            {props.freshNotificationsCount > 0 && (
              <NotifierCount>{props.freshNotificationsCount}</NotifierCount>
            )}
            <Notifications />
          </NotifierWrap>
          <UserProfile
            sex={props.sex}
            profileLink={props.profileLink}
            name={props.name}
            avatar={props.avatar}
            t={t}
            logout={props.logout}
          />
        </RightCol>
      </Wrap>
    </>
  );
};
AuthHeader.propTypes = {
  name: PT.string,
  avatar: PT.string,
  profileLink: PT.string,
  messengerLink: PT.object,
  unreadNotifications: PT.number,
  freshNotificationsCount: PT.number.isRequired,
  freshMessagesCount: PT.number.isRequired,
  roleSpecificLinks: PT.array.isRequired,

  openCreateTask: PT.func.isRequired,
  t: PT.func.isRequired,
  logout: PT.func.isRequired,
};

const NotificationsIcon = ({ color = "currentColor", ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    fill="none"
    viewBox="0 0 24 24"
    {...props}
  >
    <path
      fill={color}
      fillRule="evenodd"
      d="M18.707 8.796c0 1.256.332 1.997 1.063 2.85.553.628.73 1.435.73 2.31 0 .874-.287 1.704-.863 2.378a4.537 4.537 0 0 1-2.9 1.413c-1.571.134-3.143.247-4.736.247-1.595 0-3.166-.068-4.737-.247a4.532 4.532 0 0 1-2.9-1.413 3.616 3.616 0 0 1-.864-2.378c0-.875.178-1.682.73-2.31.754-.854 1.064-1.594 1.064-2.85V8.37c0-1.682.42-2.781 1.283-3.858C7.861 2.942 9.919 2 11.956 2h.09c2.08 0 4.204.987 5.466 2.625.82 1.054 1.195 2.108 1.195 3.745v.426ZM9.074 20.061c0-.504.462-.734.89-.833.5-.106 3.545-.106 4.045 0 .428.099.89.33.89.833-.025.48-.306.904-.695 1.174a3.635 3.635 0 0 1-1.713.731 3.795 3.795 0 0 1-1.008 0 3.618 3.618 0 0 1-1.714-.732c-.39-.269-.67-.694-.695-1.173Z"
      clipRule="evenodd"
    />
  </svg>
);

const MessagesIcon = ({ color = "currentColor", ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    fill="none"
    viewBox="0 0 24 24"
    {...props}
  >
    <path
      fill={color}
      fillRule="evenodd"
      d="M16.94 3A5.057 5.057 0 0 1 22 8.05v7.9c0 2.79-2.27 5.05-5.06 5.05H7.06A5.057 5.057 0 0 1 2 15.95v-7.9A5.05 5.05 0 0 1 7.06 3h9.88Zm1.59 6.54.08-.08a.773.773 0 0 0-.01-1 .84.84 0 0 0-.53-.26.76.76 0 0 0-.56.2L13 12a1.566 1.566 0 0 1-2 0L6.5 8.4a.76.76 0 0 0-1 .07c-.27.27-.3.7-.07 1l.13.13 4.55 3.55a3.143 3.143 0 0 0 3.91 0l4.51-3.61Z"
      clipRule="evenodd"
    />
  </svg>
);

export default AuthHeader;
